import React, { useCallback } from 'react';
import useImportAssetFileMutation from '../../hooks/useImportAssetFileMutation';
import { ImportFileOutputData, Tenant } from '#/graphql/types';
import { unknownMappingValue } from '#/common/components/ColumnMapper/ColumnMapper';
import SubmitConfirmModal from '../modals/SubmitConfirmModal';
import { inUseStatuses } from '#/modules/asset/model/utils/constants';
import { buildTenantImportConfig } from '../base/commonImportConfigs';

export type MappingConfig =
  | string
  | { field?: string; defaultValue?: string | null; relatedTo?: string }
  | undefined;

export type FieldMapConfig = Record<string, MappingConfig>;

export type LocationLevelConfig = { name: MappingConfig; id?: string };

export type StatusGate = {
  column: string;
  activeValue: string;
  pendingValue: string;
  retiredValue: string;
  entityLabel: string;
};

export type ObjectImportConfig = {
  model: string;
  thirdPartyId: string;
  thirdPartyIdSource?: string;
  allowUnknownRecords?: boolean;
  allowDuplicateKeys?: boolean;
  columnMap: FieldMapConfig;
  hierarchy?: {
    country: LocationLevelConfig;
    state: LocationLevelConfig;
    city: LocationLevelConfig;
  };
  updateColumns?: string[];
  uniqueForTenant?: boolean;
  onlyForMapping?: boolean;
  fixedId?: string;
  statusGate?: StatusGate;
};

interface SubmitModalProps {
  visible: boolean;
  uploadedFile: File | null;
  mapping: Record<string, string>;
  uniqueField: string;
  selectedTenant?: Tenant;
  onHiding: () => void;
  onImportStart: () => void;
  onSuccess: (importResult: ImportFileOutputData) => void;
  onFailed: (error: unknown) => void;
}

const SubmitModal: React.FC<SubmitModalProps> = ({
  visible,
  uploadedFile,
  mapping,
  uniqueField,
  selectedTenant,
  onHiding,
  onImportStart,
  onSuccess,
  onFailed,
}) => {
  const { importAssetFile } = useImportAssetFileMutation();

  // Generate the import config based on the mapping
  const generateImportConfig = useCallback((): string => {
    if (!selectedTenant || !mapping || Object.keys(mapping).length === 0) {
      return '';
    }

    const isColumnMapped = (field: string) =>
      !!mapping[field] && mapping[field] !== unknownMappingValue.key;

    const tenantImportConfig = buildTenantImportConfig(selectedTenant);

    const manufacturerImportConfig: ObjectImportConfig = {
      model: 'ManufacturerModel',
      thirdPartyId: 'name',
      allowUnknownRecords: true,
      allowDuplicateKeys: true,
      columnMap: {
        name: mapping['manufacturer'],
        thirdPartyId: mapping['manufacturer'],
        contactPerson: { defaultValue: '' },
        inUseStatus: { defaultValue: inUseStatuses.inUse.key },
        tenantId: {
          relatedTo: 'TenantModel',
        },
      },
      updateColumns: ['name'],
      uniqueForTenant: true,
      statusGate: {
        column: 'inUseStatus',
        activeValue: inUseStatuses.inUse.key,
        pendingValue: inUseStatuses.newlyCreated.key,
        retiredValue: inUseStatuses.deprecated.key,
        entityLabel: 'Manufacturer',
      },
    };

    const assetTypeImportConfig: ObjectImportConfig = {
      model: 'AssetTypeModel',
      thirdPartyId: 'name',
      allowUnknownRecords: true,
      allowDuplicateKeys: true,
      columnMap: {
        name: mapping['asset_type'],
        thirdPartyId: mapping['asset_type'],
        defaultRiskScore: { defaultValue: '0' },
        category: { defaultValue: 'other' },
        inUseStatus: { defaultValue: inUseStatuses.inUse.key },
        tenantId: {
          relatedTo: 'TenantModel',
        },
      },
      updateColumns: ['name'],
      uniqueForTenant: true,
      statusGate: {
        column: 'inUseStatus',
        activeValue: inUseStatuses.inUse.key,
        pendingValue: inUseStatuses.newlyCreated.key,
        retiredValue: inUseStatuses.deprecated.key,
        entityLabel: 'Asset Type',
      },
    };

    const modelImportConfig: ObjectImportConfig = {
      model: 'ModelModel',
      thirdPartyId: 'name',
      allowUnknownRecords: true,
      allowDuplicateKeys: true,
      columnMap: {
        name: mapping['model'],
        thirdPartyId: mapping['model'],
        inUseStatus: { defaultValue: inUseStatuses.inUse.key },
        tenantId: {
          relatedTo: 'TenantModel',
        },
        manufacturerId: {
          relatedTo: 'ManufacturerModel',
        },
        assetTypeId: {
          relatedTo: 'AssetTypeModel',
        },
      },
      updateColumns: ['name', 'manufacturerId', 'assetTypeId'],
      uniqueForTenant: true,
      statusGate: {
        column: 'inUseStatus',
        activeValue: inUseStatuses.inUse.key,
        pendingValue: inUseStatuses.newlyCreated.key,
        retiredValue: inUseStatuses.deprecated.key,
        entityLabel: 'Model',
      },
    };

    const locationImportConfig: ObjectImportConfig = {
      model: 'LocationModel',
      thirdPartyId: 'name',
      columnMap: {
        name: mapping['location_building'],
        tenantId: { defaultValue: selectedTenant.id },
        address: isColumnMapped('location_address') ? mapping['location_address'] : undefined,
      },
      hierarchy: {
        country: { name: mapping['location_country'] },
        state: { name: mapping['location_state'] },
        city: { name: mapping['location_city'] },
      },
      updateColumns: ['name'],
      uniqueForTenant: true,
    };

    const assetImportConfig: ObjectImportConfig = {
      model: 'AssetModel',
      thirdPartyId: 'thirdPartyId2',
      columnMap: {
        thirdPartyId2:
          uniqueField === 'serial_number' ? mapping['serial_number'] : mapping['asset_tag'],
        // Mandatory fields
        assetTag: mapping['asset_tag'],
        serialNumber: isColumnMapped('serial_number')
          ? mapping['serial_number']
          : { defaultValue: unknownMappingValue.label },
        state: { defaultValue: 'installed' },
        description: isColumnMapped('description')
          ? { field: mapping['description'], defaultValue: '' }
          : { defaultValue: unknownMappingValue.label },
        // Optional fields
        installDate: isColumnMapped('install_date') ? mapping['install_date'] : undefined,
        condition: isColumnMapped('condition') ? mapping['condition'] : undefined,
        acquisitionCostValue: isColumnMapped('acquisition_cost_value')
          ? mapping['acquisition_cost_value']
          : undefined,
        maintenanceCostValue: isColumnMapped('maintenance_cost_value')
          ? mapping['maintenance_cost_value']
          : undefined,
        usefulLife: isColumnMapped('useful_life') ? mapping['useful_life'] : undefined,
        // Related entities
        tenantId: {
          relatedTo: 'TenantModel',
        },
        modelId: {
          relatedTo: 'ModelModel',
        },
        makeId: {
          relatedTo: 'ManufacturerModel',
        },
        assetTypeId: {
          relatedTo: 'AssetTypeModel',
        },
        locationId: {
          relatedTo: 'LocationModel',
        },
      },
      updateColumns: [
        'assetTag',
        'serialNumber',
        'description',
        'installDate',
        'condition',
        'acquisitionCostValue',
        'maintenanceCostValue',
        'usefulLife',
        'modelId',
        'makeId',
        'assetTypeId',
        'locationId',
      ],
      uniqueForTenant: true,
    };

    // Base config structure that mirrors backend's salesForceAssetConfig
    const config: ObjectImportConfig[] = [
      tenantImportConfig,
      manufacturerImportConfig,
      assetTypeImportConfig,
      modelImportConfig,
      locationImportConfig,
      assetImportConfig,
    ];

    return JSON.stringify(config);
  }, [uniqueField, mapping, selectedTenant]);

  return (
    <SubmitConfirmModal
      visible={visible}
      uploadedFile={uploadedFile}
      selectedTenant={selectedTenant}
      entityLabelPlural="Assets"
      entityLabelSingularLower="asset"
      generateImportConfig={generateImportConfig}
      importFile={importAssetFile}
      onHiding={onHiding}
      onImportStart={onImportStart}
      onSuccess={onSuccess}
      onFailed={onFailed}
    />
  );
};

export default SubmitModal;
